<template>
  <div class="ml-3">
    <v-card>
      <v-card-title>{{ title }}</v-card-title>
      <v-card-text class="pa-0">
        <base-data-grid
          :ref="grid.key"
          :gridKey="grid.key"
          :dataSource="grid.dataSource"
          :columns="grid.columns"
          @on-add-click="add"
          :allowMultipleSelection="true"
          :exportFileName="title"
        >
          <template slot="customActionTemplates" slot-scope="row">
            <v-tooltip top>
              <template v-slot:activator="{ on }">
                <a
                  v-on="on"
                  @click.prevent="editContentTypeRights(row.data.item)"
                  class="px-2"
                >
                  <v-icon color="blue">mdi-security</v-icon>
                </a>
              </template>
              <span>{{ $t("ContentTypeAccessRights") }}</span>
            </v-tooltip>
            <v-tooltip top>
              <template v-slot:activator="{ on }">
                <a
                  v-on="on"
                  @click.prevent="editExportConfiguration(row.data.item)"
                  class="px-2"
                >
                  <v-icon color="blue-grey">mdi-cog-transfer-outline</v-icon>
                </a>
              </template>
              <span>{{ $t("ExportConfiguration") }}</span>
            </v-tooltip>
            <v-tooltip top>
              <template v-slot:activator="{ on }">
                <a
                  v-on="on"
                  @click.prevent="editAlerts(row.data.item)"
                  class="px-2"
                >
                  <v-icon color="warning">mdi-bell</v-icon>
                </a>
              </template>
              <span>{{ $t("Alerts") }}</span>
            </v-tooltip>
            <v-tooltip top>
              <template v-slot:activator="{ on }">
                <a
                  v-on="on"
                  @click.prevent="editFieldsRights(row.data.item)"
                  class="px-2"
                >
                  <v-icon color="blue">mdi-server-security</v-icon>
                </a>
              </template>
              <span>{{ $t("FieldsAccessRights") }}</span>
            </v-tooltip>
            <v-tooltip top>
              <template v-slot:activator="{ on }">
                <a
                  v-on="on"
                  class="px-2"
                  @click="editAutoImportConfigs(row.data.item)"
                >
                  <v-icon color="blue">mdi-import</v-icon>
                </a>
              </template>
              <span>{{ $t("AutoImport") }}</span>
            </v-tooltip>
            <v-tooltip top>
              <template v-slot:activator="{ on }">
                <a
                  v-on="on"
                  @click.prevent="editItem(row.data.item)"
                  class="px-2"
                >
                  <v-icon color="primary">mdi-pencil</v-icon>
                </a>
              </template>
              <span>{{ $t("Edit") }}</span>
            </v-tooltip>
            <v-tooltip top>
              <template v-slot:activator="{ on }">
                <a
                  v-on="on"
                  @click.prevent="deleteItem(row.data.item)"
                  class="px-2"
                >
                  <v-icon color="red">mdi-delete</v-icon>
                </a>
              </template>
              <span>{{ $t("Delete") }}</span>
            </v-tooltip>
          </template>
          <template v-slot:action-buttons>
            <v-btn small color="primary" @click.prevent="onImport">
              {{ $t("Import") }}
              <v-icon>mdi-application-import</v-icon>
            </v-btn>
            <v-btn small color="primary" @click.prevent="onExport">
              {{ $t("Export") }}
              <v-icon>mdi-application-export</v-icon>
            </v-btn>
          </template>
        </base-data-grid>
      </v-card-text>
    </v-card>
    <ExportConfigModal :ref="exportConfigModalKey" />
    <RightsModal :ref="contentTypeRightsModalKey" />
    <FieldsRightsModal :ref="fieldsRightsModalKey" />
    <ContentTypeImportModal
      :ref="contentTypeImportModalKey"
      @on-imported="onItemSaved"
    />
    <AlertsModal :ref="alertsModalKey" />
    <AutoImportConfigModal :ref="autoImportConfigModalKey" />
  </div>
</template>

<script>
import ContentTypeService from "@/services/ContentTypeService";
import ExportConfigModal from "@/components/administration/contentType/exportConfig/ExportConfigModal.vue";
import RightsModal from "@/components/administration/contentType/rights/RightsModal.vue";
import FieldsRightsModal from "@/components/administration/contentType/rights/FieldsRightsModal.vue";
import ContentTypeImportModal from "@/components/administration/contentType/ContentTypeImportModal.vue";
import AlertsModal from "@/components/administration/contentType/alerts/AlertsModal.vue";
import AutoImportConfigModal from "@/components/administration/contentType/autoImport/ContentTypeAutoImportConfigModal.vue";
import Utils from "@/ecm/common/Utils";

export default {
  components: {
    ExportConfigModal,
    RightsModal,
    FieldsRightsModal,
    ContentTypeImportModal,
    AlertsModal,
    AutoImportConfigModal,
  },
  data() {
    return {
      title: this.$t("ContentTypes"),
      grid: {
        key: "contentTypeGrid",
        dataSource: ContentTypeService.getGrid(),
        columns: [
          {
            field: "Name",
            caption: this.$t("Name"),
            type: "string",
          },
          {
            field: "Version",
            caption: this.$t("Version"),
            type: "string",
          },
        ],
      },
      autoImportConfigModalKey: "autoImportConfigModal",
      contentTypeRightsModalKey: "contentTypeRightsModal",
      exportConfigModalKey: "exportConfigModal",
      alertsModalKey: "alertsModal",
      fieldsRightsModalKey: "fieldsRightsModal",
      contentTypeImportModalKey: "contentTypeImportModal",
    };
  },
  computed: {
    gridInstance() {
      return this.$refs[this.grid.key];
    },
  },
  methods: {
    editContentTypeRights(dto) {
      this.$refs[this.contentTypeRightsModalKey].edit(dto);
    },
    editExportConfiguration(dto) {
      this.$refs[this.exportConfigModalKey].edit(dto);
    },
    editAlerts(dto) {
      this.$refs[this.alertsModalKey].edit(dto);
    },
    editFieldsRights(dto) {
      this.$refs[this.fieldsRightsModalKey].edit(dto);
    },
    editAutoImportConfigs(dto) {
      this.$refs[this.autoImportConfigModalKey].edit(dto);
    },
    onImport() {
      this.$refs[this.contentTypeImportModalKey].new();
    },
    editItem(dto) {
      this.$router.push(`/administration/content-type/${dto.Id}`);
    },
    deleteItem(dto) {
      this.$confirm(this.$t("ItemDeleteConfirmation"), {
        title: this.$t("Delete"),
        icon: "mdi-alert",
        color: "red",
        buttonTrueText: this.$t("Delete"),
        buttonFalseText: this.$t("Cancel"),
      }).then((res) => {
        if (res == 1) {
          ContentTypeService.delete(dto.Id).then((result) => {
            if (result) {
              this.gridInstance.refresh();
              Utils.toast.success(
                this.$t("ContentType") + " " + this.$t("DeletedSuccessfully")
              );
            }
          });
        }
      });
    },
    add() {
      this.$router.push("/administration/content-type");
    },
    onItemSaved() {
      this.$store.dispatch("account/getUserContentTypes");
      this.gridInstance.refresh();
    },
    onExport() {
      var selectedItems = this.gridInstance.selectedRowKeys;
      if (selectedItems.length == 0) {
        Utils.toast.warning(this.$t("ItemsSelectionError"));
        return;
      }
      ContentTypeService.export(selectedItems);
    },
  },
};
</script>